import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Modal } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash, faAngleLeft, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import useFetch from '../../../../hooks/useFetch';
import PaginatedItems from '../../../../components/administracao/Pagination';
import Swal from 'sweetalert2';

const ITENS_POR_PAGINA = 20;

export default function GerenciarSubcategorias() {
    const { id_cat } = useParams();
    const { get, del } = useFetch();

    const [state, setState] = useState({
        subcategorias: [],
        count: 0,
        page: 1,
        nomeCategoriaPai: "",
        loading: true,
        mostrarModal: false,
        registroAExcluir: {}
    });

    useEffect(() => {
        async function carregar() {
            let [resultado, categoriaPai] = await Promise.all([
                buscarSubcategorias(1),
                buscarCategoriaPai(),
            ]);
            setState(s => ({
                ...s,
                subcategorias: resultado.data,
                count: resultado.count,
                page: 1,
                nomeCategoriaPai: categoriaPai,
                loading: false
            }));
        }
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id_cat]);

    async function buscarSubcategorias(page = 1) {
        try {
            let response = await get(`/subcategoriafotografia?id_categoria=${id_cat}&page=${page}`);
            if (response.ok) return { data: response.result.data, count: response.result.count ?? 0 };
            return { data: [], count: 0 };
        } catch (error) {
            console.error('Erro no buscarSubcategorias():', error);
            return { data: [], count: 0 };
        }
    }

    async function buscarCategoriaPai() {
        try {
            let response = await get(`/categoriafotografia/${id_cat}`);
            if (response.ok && response.result.data) return response.result.data.nome_categoria;
            return "";
        } catch (error) {
            console.error('Erro no buscarCategoriaPai():', error);
            return "";
        }
    }

    async function trocarPagina(page) {
        // Atualiza a página imediatamente (destaque do paginador em sincronia)
        // e troca as linhas quando chegarem, sem spinner, pra ficar suave.
        setState(s => ({ ...s, page }));
        let resultado = await buscarSubcategorias(page);
        setState(s => ({ ...s, subcategorias: resultado.data, count: resultado.count, page }));
    }

    async function excluirSubcategoria() {
        try {
            let response = await del('/subcategoriafotografia/' + state.registroAExcluir.id_subcategoria);
            if (response.status === 200) {
                // Se excluiu o último item da página atual, volta uma página.
                let paginaAlvo = (state.subcategorias.length === 1 && state.page > 1) ? state.page - 1 : state.page;
                let resultado = await buscarSubcategorias(paginaAlvo);
                setState(s => ({ ...s, subcategorias: resultado.data, count: resultado.count, page: paginaAlvo, mostrarModal: false, registroAExcluir: {} }));
            } else if (response.status === 409) {
                setState({ ...state, mostrarModal: false });
                Swal.fire({ title: "Não foi possível excluir", text: response.result?.message ?? "", icon: "error", confirmButtonColor: "#587b7f" });
            } else {
                console.error('Erro no excluirSubcategoria()');
                Swal.fire({ title: "Não foi possível excluir a subcategoria", text: "Erro inesperado, tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirSubcategoria():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="mb-4">
                <Link to="/admin/iconografico/categorias" className="btn-back-modern">
                    <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Voltar às Categorias
                </Link>
            </div>

            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Subcategorias</h2>
                    <p className="admin-subtitle">Organizando itens de: <strong>{state.nomeCategoriaPai || "..."}</strong></p>
                </div>
                <Link to={`/admin/iconografico/categorias/${id_cat}/subcategorias/criar`} className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nova Subcategoria
                </Link>
            </div>

            <Card className="table-card-admin mt-4">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome da Subcategoria</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="2" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.subcategorias.length > 0 ? (
                                state.subcategorias.map((item) => (
                                    <tr key={item.id_subcategoria}>
                                        <td data-label="Nome da Subcategoria" className="fw-bold">
                                            <FontAwesomeIcon icon={faLayerGroup} className="me-2 text-muted opacity-50" />
                                            {item.nome_subcategoria}
                                        </td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon edit" to={`/admin/iconografico/categorias/${id_cat}/subcategorias/${item.id_subcategoria}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                                <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: item })}><FontAwesomeIcon icon={faTrash} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="2" className="text-center py-4">Nenhuma subcategoria encontrada para esta categoria.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {state.count > ITENS_POR_PAGINA && (
                <div className="mt-4">
                    <PaginatedItems
                        itemCount={state.count}
                        itemsPerPage={ITENS_POR_PAGINA}
                        handlePageChange={trocarPagina}
                        currentPage={state.page}
                    />
                </div>
            )}

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Subcategoria</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja excluir a subcategoria <strong>{state.registroAExcluir.nome_subcategoria}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirSubcategoria}>Confirmar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
